import { createReducer, on } from '@ngrx/store';
import * as WalletActions from './wallet.actions';
import { PaginationResponse } from '@shared/models/common.model';

import { WalletListItem } from '@shared/models/wallet.model';
import {
  Asset,
  WalletTransactionItem,
  WalletWithdrawalPrevalidateResponse,
} from '../models/wallet.model';

export interface WalletState {
  wallets: WalletListItem[];
  walletsLoading: boolean;
  walletsError: string | null;
  walletsPagination: PaginationResponse | null;
  assets: Asset[];
  assetsLoading: boolean;
  assetsError: string | null;
  walletTransactions: WalletTransactionItem[];
  walletTransactionsLoading: boolean;
  walletTransactionsError: string | null;
  walletTransactionsPagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | null;

  operationLoading: boolean;
  operationError: string | null;
  operationSuccess: string | null;
  linkLoading: boolean;
  linkError: string | null;
  walletDisableLoading: boolean;
  walletDisableError: string | null;
  walletEnableLoading: boolean;
  walletEnableError: string | null;
  walletUnassignLoading: boolean;
  walletUnassignError: string | null;

  withdrawalPrevalidateLoading: boolean;
  /** Which wallet a prevalidate request is in flight for (supports multiple withdraw buttons on screen). */
  withdrawalPrevalidateWalletId: string | null;
  withdrawalPrevalidateError: string | null;
  withdrawalPrevalidateResponse: WalletWithdrawalPrevalidateResponse | null;
}

export const initialState: WalletState = {
  wallets: [],
  walletsLoading: false,
  walletsError: null,
  walletsPagination: null,
  assets: [],
  assetsLoading: false,
  assetsError: null,
  walletTransactions: [],
  walletTransactionsLoading: false,
  walletTransactionsError: null,
  walletTransactionsPagination: null,
  operationLoading: false,
  operationError: null,
  operationSuccess: null,
  linkLoading: false,
  linkError: null,
  walletDisableLoading: false,
  walletDisableError: null,
  walletEnableLoading: false,
  walletEnableError: null,
  walletUnassignLoading: false,
  walletUnassignError: null,

  withdrawalPrevalidateLoading: false,
  withdrawalPrevalidateWalletId: null,
  withdrawalPrevalidateError: null,
  withdrawalPrevalidateResponse: null,
};

export const walletReducer = createReducer(
  initialState,

  // Load Wallets
  on(WalletActions.loadWallets, state => ({
    ...state,
    walletsLoading: true,
    walletsError: null,
  })),

  // Load Wallets Success
  on(
    WalletActions.loadWalletsSuccess,
    (state, { wallets, pagination, append }) => ({
      ...state,
      wallets: append ? [...state.wallets, ...wallets] : wallets,
      walletsPagination: pagination,
      walletsLoading: false,
      walletsError: null,
    })
  ),

  // Load Wallets Failure
  on(WalletActions.loadWalletsFailure, (state, { error }) => ({
    ...state,
    walletsLoading: false,
    walletsError: error,
  })),

  // Load Assets
  on(WalletActions.loadAssets, state => ({
    ...state,
    assetsLoading: true,
    assetsError: null,
  })),

  on(WalletActions.loadAssetsSuccess, (state, { assets }) => ({
    ...state,
    assets,
    assetsLoading: false,
    assetsError: null,
  })),

  on(WalletActions.loadAssetsFailure, (state, { error }) => ({
    ...state,
    assetsLoading: false,
    assetsError: error,
  })),

  // Create Wallet
  on(WalletActions.createWallet, state => ({
    ...state,
    operationLoading: true,
    operationError: null,
    operationSuccess: null,
  })),

  on(WalletActions.createWalletSuccess, (state, { message }) => ({
    ...state,
    operationLoading: false,
    operationError: null,
    operationSuccess: message,
  })),

  on(WalletActions.createWalletFailure, (state, { error }) => ({
    ...state,
    operationLoading: false,
    operationError: error,
    operationSuccess: null,
  })),

  // Update Wallet
  on(WalletActions.updateWallet, state => ({
    ...state,
    operationLoading: true,
    operationError: null,
    operationSuccess: null,
  })),

  on(WalletActions.updateWalletSuccess, (state, { message }) => ({
    ...state,
    operationLoading: false,
    operationError: null,
    operationSuccess: message,
  })),

  on(WalletActions.updateWalletFailure, (state, { error }) => ({
    ...state,
    operationLoading: false,
    operationError: error,
    operationSuccess: null,
  })),

  // Disable Wallet
  on(WalletActions.disableWallet, state => ({
    ...state,
    operationLoading: true,
    operationError: null,
    operationSuccess: null,
  })),

  on(WalletActions.disableWalletSuccess, (state, { message }) => ({
    ...state,
    operationLoading: false,
    operationError: null,
    operationSuccess: message,
  })),

  on(WalletActions.disableWalletFailure, (state, { error }) => ({
    ...state,
    operationLoading: false,
    operationError: error,
    operationSuccess: null,
  })),

  // Enable Wallet
  on(WalletActions.enableWallet, state => ({
    ...state,
    operationLoading: true,
    operationError: null,
    operationSuccess: null,
  })),

  on(WalletActions.enableWalletSuccess, (state, { message }) => ({
    ...state,
    operationLoading: false,
    operationError: null,
    operationSuccess: message,
  })),

  on(WalletActions.enableWalletFailure, (state, { error }) => ({
    ...state,
    operationLoading: false,
    operationError: error,
    operationSuccess: null,
  })),

  on(WalletActions.retryWalletKeygen, state => ({
    ...state,
    operationLoading: true,
    operationError: null,
    operationSuccess: null,
  })),

  on(WalletActions.retryWalletKeygenSuccess, (state, { message }) => ({
    ...state,
    operationLoading: false,
    operationError: null,
    operationSuccess: message,
  })),

  on(WalletActions.retryWalletKeygenFailure, (state, { error }) => ({
    ...state,
    operationLoading: false,
    operationError: error,
    operationSuccess: null,
  })),

  // Clear operation success
  on(WalletActions.clearOperationSuccess, state => ({
    ...state,
    operationSuccess: null,
  })),

  // Load Wallet Transactions
  on(WalletActions.loadWalletTransactions, state => ({
    ...state,
    walletTransactionsLoading: true,
    walletTransactionsError: null,
  })),

  on(WalletActions.loadWalletTransactionsSuccess, (state, { response }) => ({
    ...state,
    walletTransactions: response.data,
    walletTransactionsPagination: response.pagination,
    walletTransactionsLoading: false,
    walletTransactionsError: null,
  })),

  on(WalletActions.loadWalletTransactionsFailure, (state, { error }) => ({
    ...state,
    walletTransactionsLoading: false,
    walletTransactionsError: error,
  })), // Link to Wallet
  on(WalletActions.linkWhitelistAddressToWallet, state => ({
    ...state,
    linkLoading: true,
    linkError: null,
  })),

  on(WalletActions.linkWhitelistAddressToWalletSuccess, state => ({
    ...state,
    linkLoading: false,
    linkError: null,
  })),

  on(WalletActions.linkWhitelistAddressToWalletFailure, (state, { error }) => ({
    ...state,
    linkLoading: false,
    linkError: error,
  })),
  // Wallet-specific Disable
  on(WalletActions.disableWalletWhitelistAddress, state => ({
    ...state,
    walletDisableLoading: true,
    walletDisableError: null,
  })),

  on(WalletActions.disableWalletWhitelistAddressSuccess, state => ({
    ...state,
    walletDisableLoading: false,
    walletDisableError: null,
  })),

  on(
    WalletActions.disableWalletWhitelistAddressFailure,
    (state, { error }) => ({
      ...state,
      walletDisableLoading: false,
      walletDisableError: error,
    })
  ),

  // Wallet-specific Enable
  on(WalletActions.enableWalletWhitelistAddress, state => ({
    ...state,
    walletEnableLoading: true,
    walletEnableError: null,
  })),

  on(WalletActions.enableWalletWhitelistAddressSuccess, state => ({
    ...state,
    walletEnableLoading: false,
    walletEnableError: null,
  })),

  on(WalletActions.enableWalletWhitelistAddressFailure, (state, { error }) => ({
    ...state,
    walletEnableLoading: false,
    walletEnableError: error,
  })),

  on(WalletActions.unassignWhitelistAddressFromWallet, state => ({
    ...state,
    walletUnassignLoading: true,
    walletUnassignError: null,
  })),

  on(WalletActions.unassignWhitelistAddressFromWalletSuccess, state => ({
    ...state,
    walletUnassignLoading: false,
    walletUnassignError: null,
  })),

  on(
    WalletActions.unassignWhitelistAddressFromWalletFailure,
    (state, { error }) => ({
      ...state,
      walletUnassignLoading: false,
      walletUnassignError: error,
    })
  ),

  on(WalletActions.prevalidateWithdrawal, (state, { walletId }) => ({
    ...state,
    withdrawalPrevalidateLoading: true,
    withdrawalPrevalidateWalletId: walletId,
    withdrawalPrevalidateError: null,
    withdrawalPrevalidateResponse: null,
  })),

  on(WalletActions.prevalidateWithdrawalSuccess, (state, { response }) => ({
    ...state,
    withdrawalPrevalidateLoading: false,
    withdrawalPrevalidateWalletId: null,
    withdrawalPrevalidateError: null,
    withdrawalPrevalidateResponse: response,
  })),

  on(WalletActions.prevalidateWithdrawalFailure, (state, { error }) => ({
    ...state,
    withdrawalPrevalidateLoading: false,
    withdrawalPrevalidateWalletId: null,
    withdrawalPrevalidateError: error,
    withdrawalPrevalidateResponse: null,
  }))
);
